import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
  buttonFunctions,
} from '../../shared/Button';
import {
  searchText,
  submitText,
  cancelText,
  descriptionText,
  emailText,
  notesText,
} from '../../shared/SharedInternationalizations';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';
import { getAllAlertDevices } from '../../../redux/actions/alertDeviceActions';
import { required } from '../../../utilities/general';
import { Condition } from '../../shared/Condition';

export function UnconnectedAddEditAlertEmailForm(props) {
  const { deviceDetails, deviceType, alertDeviceCategories, onSubmit, 'data-test': dataTest } = props;

  const categoriesArr = Object.values(alertDeviceCategories);

  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    props.dispatch(getAllAlertDevices());
  }, []); // eslint-disable-line

  /**
   * Function to set the search value for active filtering for users
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUsersFilter - Updates local state with the value typed into the search bar
   */
  const categorySearchFilter = (event) => {
    const value = event.target.value;
    setCategoryFilter(value);
  };

  const [showCategoryHelpText, setShowCategoryHelpText] = useState(false);
  const categoryHelpTextRef = useRef();
  function toggleCategoryHelpText(value) {
    setShowCategoryHelpText(value);
  }
  const categoryHelpText = (
    <FormattedMessage
      id="addEditAlertEmail.categoryHelpText"
      defaultMessage="The device category."
      description="Help text."
    />
  );
  const [showEmailHelpText, setShowEmailHelpText] = useState(false);
  const emailHelpTextRef = useRef();
  function toggleEmailHelpText(value) {
    setShowEmailHelpText(value);
  }
  const emailHelpText = (
    <FormattedMessage
      id="addEditAlertEmail.emailHelpText"
      defaultMessage="The email address SARA will use to contact this user."
      description="Help text."
    />
  );

  const [showDescriptionHelpText, setShowDescriptionHelpText] = useState(false);
  const descriptionHelpTextRef = useRef();
  function toggleDescriptionHelpText(value) {
    setShowDescriptionHelpText(value);
  }
  const descriptionHelpText = (
    <FormattedMessage
      id="addEditAlertEmail.descriptionHelpText"
      defaultMessage="The description for this email device."
      description="Help text."
    />
  );

  const [showNotesHelpText, setShowNotesHelpText] = useState(false);
  const notesHelpTextRef = useRef();
  function toggleNotesHelpText(value) {
    setShowNotesHelpText(value);
  }
  const notesHelpText = (
    <FormattedMessage
      id="addEditAlertEmail.notesHelpText"
      defaultMessage="This is a place to record notes about the user."
      description="Help text."
    />
  );

  const [showNotifyOnClearHelpText, setShowNotifyOnClearHelpText] = useState(
    false
  );
  const notifyOnClearHelpTextRef = useRef();
  function toggleNotifyOnClearHelpText(value) {
    setShowNotifyOnClearHelpText(value);
  }
  const notifyOnClearHelpText = (
    <FormattedMessage
      id="addEditAlertEmail.notifyOnClearHelpText"
      defaultMessage="This will allow you to receive an email once the alarm is confirmed"
      description="Help text."
    />
  );
  const [
    showUseShortMessageHelpText,
    setShowUseShortMessageHelpText,
  ] = useState(false);
  const useShortMessageHelpTextRef = useRef();
  function toggleUseShortMessageHelpText(value) {
    setShowUseShortMessageHelpText(value);
  }
  const useShortMessageHelpText = (
    <FormattedMessage
      id="addEditAlertEmail.useShortMessageHelpText"
      defaultMessage="Recommended for email-to-text messages"
      description="Help text."
    />
  );

  /**
   * Function to generate the users table
   * @returns {object} - The table of users
   */
  const alertDeviceCategoryTable = (values) => {
    // map over the users and put into the format that is required for the table component
    const initialFilterList = categoriesArr.filter((d) => {
      if (values.alert_device_category === d.alert_device_category) {
        return false;
      } else {
        return true;
      }
    });

    const categoryArray = initialFilterList.map((d) => {
      return {
        selector: {
          sortBy: d.alert_device_category,
          display: d.alert_device_category,
        },
        description: {
          sortBy: d.description.toUpperCase(),
          display: d.description,
        },
      };
    });

    let searchedCategories = [];
    // Take the array and filter based on the text entered in the search bar.
    if (categoryFilter !== '') {
      searchedCategories = categoryArray.filter((item) => {
        let filterResult = false;
        // Make the device name uppercase for case insensitivity
        const description = item.description.display.toUpperCase();

        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        if (description.includes(categoryFilter.toUpperCase())) {
          filterResult = true;
        }
        return filterResult;
      });
    } else {
      searchedCategories = categoryArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },

      {
        isSortable: true,
        sortBy: 'description',
        display: descriptionText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="alert_device_category"
        selectorParamValue="alert_device_category"
        sortParam="description"
        tableData={searchedCategories}
        selectorType={selectorTypes.radio}
        tableStyles="table__constrained table-layout__1-column--selector"
        data-test="alert-device-category-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedAlertDeviceCategoryTable = (values) => {
    let table;
    let categoryId = values.alert_device_category;

    let deviceRows;
    if (categoryId) {
      const filteredDevices = [];

      let thisId = categoryId;
      if (typeof categoryId === 'string') {
        thisId = parseInt(categoryId);
      }
      if (alertDeviceCategories[thisId]) {
        filteredDevices.push(alertDeviceCategories[thisId]);
      }

      deviceRows = filteredDevices.map((d) => {
        const removeButton = (
          <label>
            <Field
              className="visually-hidden"
              name="alert_device_category"
              component="input"
              type="radio"
              value={d.alert_device_category}
            />
            <SvgXIcon />
          </label>
        );

        return {
          button: {
            sortBy: '',
            display: removeButton,
          },

          description: {
            sortBy: d.description.toUpperCase(),
            display: d.description,
          },
        };
      });

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: false,
          sortBy: 'description',
          display: descriptionText,
        },
      ];

      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="description"
          tableData={deviceRows}
          tableStyles="table__constrained table-layout__1-column--selector"
          data-test="alert-device-selected-category-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  let details = {};

  if (!deviceDetails) {
    details = {
      alert_device_type: deviceType,
      user: deviceDetails.user || '',
    };
  } else {
    details = {
      alert_device_id: deviceDetails.alert_device_id,
      alert_description: deviceDetails.alert_description || '',
      email_address: deviceDetails.email_address || '',
      short_msg: deviceDetails.short_msg,
      notify_on_clear: deviceDetails.notify_on_clear,
      interface_name: deviceDetails.interface_name || '',
      notes: deviceDetails.notes || '',
      alert_device_type: deviceDetails.alert_device_type,
      user: deviceDetails.user || '',
      subtype: deviceDetails.subtype || 0,
      resident_id: deviceDetails.resident_id || 0,
      alert_device_category: deviceDetails.alert_device_category || null,
    };
  }

  return (
    <React.Fragment>
      <Form
        onSubmit={onSubmit}
        keepDirtyOnReinitialize={true}
        initialValues={{
          ...details,
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form
            data-test={dataTest || "add-edit-alert-email-form"}
            onSubmit={handleSubmit}
            className="associate-users-units__form-container"
            noValidate="novalidate">
            <Field
              className="visually-hidden"
              name="user"
              component="input"
              type="text"
            />
            <Field
              className="visually-hidden"
              name="alert_device_type"
              component="input"
              type="number"
            />

            <div className="content-display__container">
              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label
                      className="text-large text-light text-deep-blue form-item__field-message--required"
                      htmlFor="alert_description">
                      <FormattedMessage
                        id="addEditAlertEmail.descriptionTitle"
                        defaultMessage="Description:"
                        description=""
                      />
                    </label>

                    <Field
                      className="form-item__field text-bold"
                      name="alert_description"
                      component="input"
                      type="text"
                      validate={required}>
                      {({ input, meta }) => (
                        <React.Fragment>
                          <input
                            {...input}
                            type="text"
                            className={
                              meta.error && meta.touched
                                ? 'form-item__field form-item__field--error'
                                : 'form-item__field'
                            }
                          />
                          {meta.error && meta.touched && (
                            <span className="form-item__field-message--required text-small">
                              {meta.error}
                            </span>
                          )}
                        </React.Fragment>
                      )}
                    </Field>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() =>
                        descriptionHelpTextRef.current.toggleHelpText()
                      }
                      toggled={showDescriptionHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={descriptionHelpTextRef}
                  toggleFunction={toggleDescriptionHelpText}
                  message={descriptionHelpText}
                />
              </div>

              <div className="content-display__column content-display__column--1-2">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <label
                      className="text-large text-light text-deep-blue form-item__field-message--required"
                      htmlFor="alert_description">
                      {emailText}:
                    </label>
                    <Field
                      className="form-item__field"
                      name="email_address"
                      component="input"
                      type="email"
                      pattern="^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$"
                      validate={(values) => {
                        const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
                        if (values) {
                          if (regex.test(values)) {
                            return undefined;
                          } else {
                            return 'Please use the following format: xxx@xx.xxx';
                          }
                        } else {
                          return 'This field is required.';
                        }
                      }}>
                      {({ input, meta }) => (
                        <React.Fragment>
                          <input
                            {...input}
                            type="email"
                            pattern="/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/"
                            className={
                              meta.error
                                ? 'form-item__field--noInvalid form-item__field--noInvalid--error'
                                : 'form-item__field--noInvalid'
                            }
                          />
                          {meta.error && (
                            <span className="form-item__field-message--required text-small">
                              {meta.error}
                            </span>
                          )}
                        </React.Fragment>
                      )}
                    </Field>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      onClick={() => emailHelpTextRef.current.toggleHelpText()}
                      toggled={showEmailHelpText}
                      styles="margin-left-5"
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={emailHelpTextRef}
                  toggleFunction={toggleEmailHelpText}
                  message={emailHelpText}
                />
              </div>
              <Condition when="alert_device_type" is={4}>
                <div className="content-display__column content-display__column--1-2">
                  <div className="content-display__item-container">
                    <div className="item-container__main-container">
                      <label className="text-large text-light text-deep-blue">
                        <Field
                          name="notify_on_clear"
                          component="input"
                          type="checkbox"
                          className="margin-right-10"
                        />
                        <FormattedMessage
                          id="addEditAlertEmail.notifyOnClear"
                          defaultMessage="Send a notification when an alarm is confirmed"
                          description="Set the display mode for this user"
                        />
                      </label>
                    </div>
                    <div className="item-container__info-container">
                      <HelpIcon
                        styles="margin-left-5 keep-size"
                        onClick={() =>
                          notifyOnClearHelpTextRef.current.toggleHelpText()
                        }
                        toggled={showNotifyOnClearHelpText}
                      />
                    </div>
                  </div>
                  <InlineHelpText
                    ref={notifyOnClearHelpTextRef}
                    toggleFunction={toggleNotifyOnClearHelpText}
                    message={notifyOnClearHelpText}
                  />
                </div>
                <div className="content-display__column content-display__column--1-2">
                  <div className="content-display__item-container">
                    <div className="item-container__main-container">
                      <label className="text-large text-light text-deep-blue">
                        <Field
                          name="short_msg"
                          component="input"
                          type="checkbox"
                          className="margin-right-10"
                        />
                        <FormattedMessage
                          id="addEditAlertEmail.shortMsgText"
                          defaultMessage="Use the short message format for this email"
                          description="Set the display mode for this user"
                        />
                      </label>
                    </div>
                    <div className="item-container__info-container">
                      <HelpIcon
                        styles="margin-left-5 keep-size"
                        onClick={() =>
                          useShortMessageHelpTextRef.current.toggleHelpText()
                        }
                        toggled={showUseShortMessageHelpText}
                      />
                    </div>
                  </div>
                  <InlineHelpText
                    ref={useShortMessageHelpTextRef}
                    toggleFunction={toggleUseShortMessageHelpText}
                    message={useShortMessageHelpText}
                  />
                </div>
              </Condition>

              <Condition when="alert_device_type" is={4}>
                <div className="content-display__column content-display__column--1-2">
                  <div className="content-display__item-container">
                    <div className="item-container__main-container">
                      <label className="text-large text-light text-deep-blue">
                        {notesText}:
                      </label>
                      <Field
                        className="form-item__field text-bold"
                        name="notes"
                        component="textarea"
                      />
                    </div>
                    <div className="item-container__info-container">
                      <HelpIcon
                        onClick={() =>
                          notesHelpTextRef.current.toggleHelpText()
                        }
                        toggled={showNotesHelpText}
                        styles="margin-left-5"
                      />
                    </div>
                  </div>
                  <InlineHelpText
                    ref={notesHelpTextRef}
                    toggleFunction={toggleNotesHelpText}
                    message={notesHelpText}
                  />
                </div>
              </Condition>
              <Condition when="alert_device_type" is={4}>
                <div className="content-display__column">
                  <div className="form__item-container">
                    <div className="align-center">
                      <label
                        className="text-medium text-bold"
                        htmlFor="usersId">
                        <FormattedMessage
                          id="addEditAlertEmail.alertCategoriesLabel"
                          defaultMessage="Alert Device Category:"
                          description="Devices that have been selected."
                        />
                      </label>
                      <HelpIcon
                        onClick={() =>
                          categoryHelpTextRef.current.toggleHelpText()
                        }
                        toggled={showCategoryHelpText}
                        styles="margin-left-5"
                      />
                    </div>
                  </div>
                </div>
                <div className="width-100 margin-bottom-10">
                  <InlineHelpText
                    ref={categoryHelpTextRef}
                    toggleFunction={toggleCategoryHelpText}
                    message={categoryHelpText}
                  />
                </div>
                <div className="content-display__column">
                  {selectedAlertDeviceCategoryTable(values)}

                  <div className="table__header padding-10">
                    <label
                      className="text-medium text-bold"
                      htmlFor="groupsFilter">
                      {searchText}:
                    </label>

                    <input
                      type="text"
                      id="categoryFilter"
                      name="categoryFilter"
                      onChange={categorySearchFilter}
                      className="message-table__filter-search-box margin-left-10"
                      value={categoryFilter}
                    />
                  </div>

                  {alertDeviceCategoryTable(values)}
                </div>
              </Condition>
            </div>
            <div className="width-100 margin-bottom-10"></div>
            <div className="form__section-container form__section-container--spread associate-users-units__form-buttons-container">
              <div className="form__items-group-container" />
              <div className="form__items-group-container">
                <Link
                  data-test="add-edit-alert-phone-number-form-cancel-link"
                  className="link link-text"
                  to={props.previous}>
                  {cancelText}
                </Link>
                <Button
                  buttonFunction={buttonFunctions.submit}
                  text={submitText}
                  buttonType={buttonTypes.success}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15"
                  disabled={pristine || invalid}
                />
              </div>
            </div>
          </form>
        )}
      />
    </React.Fragment>
  );
}

const mapStateToProps = (state, ownProps) => {
  const alertGroupsArr = Object.values(state.devices.assignedAlertGroups);
  return {
    alertGroupsArr,
    alertDeviceDetails: state.devices.alertDeviceDetails,
    alertDeviceCategories: state.devices.alertDeviceCategories,
  };
};
export default connect(mapStateToProps)(UnconnectedAddEditAlertEmailForm);
